import { createSlice } from '@reduxjs/toolkit';
import { forkJoin } from 'rxjs';

import type { AppDispatch } from './store';

import tripService from '../services/tripService';
import type {
  TripLeg,
  TripLegRequestV2,
  TripLegResponseItem,
  TripLegStatusLogEntry,
  TripLegAddOnLineItem,
} from './models/trip-leg.model';
import {
  TripLegStatusBC,
  TripLegStatus,
  tripStatusAttestationList,
  hasReachedStatus,
} from './models/trip-leg-status.model';
import { TripLegExceptionBC } from './models/trip-leg-exception.model';
import type { PagedList } from './models/paged-list.model';
import type { Driver } from './models/driver.model';
import type { SortModel, TripSortModel } from './models/sort-model.model';

import tripLegReservationService from '../services/tripLegReservationService';

import { attestationUtils } from '../services/attestationUtils';
import { selectTripLegExceptions } from './reference.slice';
import { DateTime } from 'luxon';

interface TripState {
  providerTrips: PagedList<TripLeg>;
  loadingProviderTrips: boolean;
  loadedProviderTrips: boolean;

  tripEligibleDriverList: PagedList<Driver>;
  loadingTripEligibleDrivers: boolean;
  loadedTripEligibleDrivers: boolean;

  assigningDriverToTrip: boolean;
  assignedDriverToTrip: boolean;

  assigningVehicleToTrip: boolean;
  assignedVehicleToTrip: boolean;

  tripLegs: PagedList<TripLegResponseItem>;
  tripLegError: string | null;
  loadingTripLegs: boolean;
  loadedTripLegs: boolean;

  updatingTripLegLogID: string | null;
  updatingTripLegLogStatus: TripLegStatusBC | null;

  tripLegEvent: TripLegStatus | null;
  tripLegExceptionEvent: boolean;
  tripLegDiscussionEvent: boolean;

  tripLegAddOns: TripLegAddOnLineItem[] | null;
  loadingTripLegAddOns: boolean;

  tripLegAddOnsMap: Record<number, TripLegAddOnLineItem[]>;
}

export const initialState: TripState = {
  providerTrips: {
    itemCount: 0,
    currentPage: 1,
    totalPages: 0,
    items: [],
  },
  loadingProviderTrips: false,
  loadedProviderTrips: false,

  tripEligibleDriverList: {
    itemCount: 0,
    currentPage: 1,
    totalPages: 0,
    items: [],
  },
  loadingTripEligibleDrivers: false,
  loadedTripEligibleDrivers: false,

  assigningDriverToTrip: false,
  assignedDriverToTrip: false,

  assigningVehicleToTrip: false,
  assignedVehicleToTrip: false,

  tripLegs: {
    itemCount: 0,
    currentPage: 1,
    totalPages: 0,
    items: [],
  },
  tripLegError: null,
  loadingTripLegs: false,
  loadedTripLegs: false,

  updatingTripLegLogID: null,
  updatingTripLegLogStatus: null,

  tripLegEvent: null,
  tripLegExceptionEvent: false,
  tripLegDiscussionEvent: false,

  tripLegAddOns: null,
  loadingTripLegAddOns: false,

  tripLegAddOnsMap: {},
};

export const slice = createSlice({
  name: 'trip',
  initialState,
  reducers: {
    setProviderTrips: (
      state: TripState,
      action: { type: string; payload: PagedList<TripLeg> },
    ) => {
      state.providerTrips = action.payload;
    },
    setLoadingProviderTrips: (
      state: TripState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProviderTrips = action.payload;
    },
    setLoadedProviderTrips: (
      state: TripState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProviderTrips = action.payload;
    },
    setTripEligibleDriverList: (
      state: TripState,
      action: { payload: PagedList<Driver> },
    ) => {
      state.tripEligibleDriverList = action.payload;
    },
    setLoadingTripEligibleDrivers: (
      state: TripState,
      action: { payload: boolean },
    ) => {
      state.loadingTripEligibleDrivers = action.payload;
    },
    setLoadedTripEligibleDrivers: (
      state: TripState,
      action: { payload: boolean },
    ) => {
      state.loadedTripEligibleDrivers = action.payload;
    },
    setAssigningDriverToTrip: (
      state: TripState,
      action: { payload: boolean },
    ) => {
      state.assigningDriverToTrip = action.payload;
    },
    setAssignedDriverToTrip: (
      state: TripState,
      action: { payload: boolean },
    ) => {
      state.assignedDriverToTrip = action.payload;
    },
    setAssigningVehicleToTrip: (
      state: TripState,
      action: { payload: boolean },
    ) => {
      state.assigningVehicleToTrip = action.payload;
    },
    setAssignedVehicleToTrip: (
      state: TripState,
      action: { payload: boolean },
    ) => {
      state.assignedVehicleToTrip = action.payload;
    },
    setUpdatingTripLegLogID: (
      state: TripState,
      action: { payload: string | null },
    ) => {
      state.updatingTripLegLogID = action.payload;
    },
    setUpdatingTripLegLogStatus: (
      state: TripState,
      action: { payload: TripLegStatusBC | null },
    ) => {
      state.updatingTripLegLogStatus = action.payload;
    },
    setTripLegEvent: (
      state: TripState,
      action: { payload: TripLegStatus | null },
    ) => {
      state.tripLegEvent = action.payload;
    },
    setTripLegExceptionEvent: (
      state: TripState,
      action: { payload: boolean },
    ) => {
      state.tripLegExceptionEvent = action.payload;
    },
    setTripLegDiscussionEvent: (
      state: TripState,
      action: { payload: boolean },
    ) => {
      state.tripLegDiscussionEvent = action.payload;
    },
    attestTripLeg: (state: TripState, action: { payload: TripLeg }) => {
      const tripLegs = [
        ...state.tripLegs.items.filter(
          (item) =>
            item.tripLegReferenceID !== action.payload.tripLegReferenceID,
        ),
      ];
      state.tripLegs = {
        ...state.tripLegs,
        items: tripLegs,
      };
    },
    upsertTripLeg: (state: TripState, action: { payload: TripLeg }) => {
      const tripLegsItems = [...state.tripLegs.items];
      const newTripLegsItems = tripLegsItems.map((item) => {
        if (item.tripLegReferenceID === action.payload.tripLegReferenceID) {
          return action.payload;
        }
      });
    },
    upsertTripLegException: (
      state: TripState,
      action: {
        payload: {
          tripLegID: number;
          tripLegExceptionID: number;
        };
      },
    ) => {
      const tripLegsItems = [...state.tripLegs.items];
      const newTripLegsItems = tripLegsItems.map((item) => {
        if (item.tripLegID === action.payload.tripLegID) {
          return {
            ...item,
            ...action.payload,
          };
        }
        return item;
      });
      state.tripLegs = {
        ...state.tripLegs,
        items: [...newTripLegsItems],
      };
    },
    upsertProviderTrip: (state: TripState, action: { payload: TripLeg }) => {
      const providerTripsItems = [...state.providerTrips.items];
      const newProviderTripsItems = providerTripsItems.map((item) => {
        if (item.tripLegReferenceID === action.payload.tripLegReferenceID) {
          // we want to update specific fields, not wholly replace in case we're missing data
          const newItem = {
            ...item,
          };
          if (action.payload.dropOffAddress) {
            newItem.dropOffAddressLine1 =
              action.payload.dropOffAddress.addressLine1 || '';
            newItem.dropOffAddressLine2 =
              action.payload.dropOffAddress.addressLine2 || '';
            newItem.dropOffCity = action.payload.dropOffAddress.city || '';
            newItem.dropOffStateCode =
              action.payload.dropOffAddress.stateCode || '';
            newItem.dropOffZipCode =
              action.payload.dropOffAddress.zipCode || '';
          }

          if (action.payload.pickUpAddress) {
            newItem.pickUpAddressLine1 =
              action.payload.pickUpAddress.addressLine1 || '';
            newItem.pickUpAddressLine2 =
              action.payload.pickUpAddress.addressLine2 || '';
            newItem.pickUpCity = action.payload.pickUpAddress.city || '';
            newItem.pickUpStateCode =
              action.payload.pickUpAddress.stateCode || '';
            newItem.pickUpZipCode = action.payload.pickUpAddress.zipCode || '';
          }
          if (action.payload.appointmentDate) {
            newItem.appointmentDate = action.payload.appointmentDate;
          }
          if (action.payload.appointmentDateTime) {
            newItem.appointmentDateTime = action.payload.appointmentDateTime;
          }
          if (action.payload.pickUpDueDateTime) {
            newItem.pickUpDueTimeUTC = action.payload.pickUpDueDateTime;
          }
          if (action.payload.driver) {
            newItem.driver = { ...action.payload.driver };
          }
          if (action.payload.driverID) {
            newItem.driverID = action.payload.driverID;
          }
          if (action.payload.status) {
            newItem.tripLegStatusBC = action.payload.status.toLocaleUpperCase();
          }
          newItem.isWillCall = action.payload.isWillCall ?? false;
          newItem.isEscalated = action.payload.isEscalated ?? false;
          newItem.isUnderDiscussion = action.payload.isUnderDiscussion ?? false;
          newItem.isException = action.payload.isException ?? false;

          return newItem;
        } else {
          return item;
        }
      });
      const providerTrips = {
        ...state.providerTrips,
        items: [...newProviderTripsItems],
      };
      state.providerTrips = { ...providerTrips };
    },
    upsertProviderDriverAssigned: (
      state: TripState,
      action: {
        type: string;
        payload: {
          tripLegReferenceID: string;
          transportationProviderID: number | null;
          driver: Driver | null;
        };
      },
    ) => {
      const items = [...state.providerTrips.items];
      const newItems = items.map((item) => {
        if (item.tripLegReferenceID === action.payload.tripLegReferenceID) {
          let newItem = { ...item } as TripLeg;
          if (action.payload.driver) {
            newItem.driver = {
              ...action.payload.driver,
              driverFirstName: action.payload.driver.firstName || '',
              driverLastName: action.payload.driver.lastName || '',
            };
          }
          if (action.payload.transportationProviderID) {
            newItem.transportationProviderID =
              action.payload.transportationProviderID;
          }
          return newItem;
        } else {
          return item;
        }
      });
      state.providerTrips = {
        ...state.providerTrips,
        items: newItems,
      };
    },
    removeFromProviderTrips: (
      state: TripState,
      action: { payload: string },
    ) => {
      const providerTripsItems = [...state.providerTrips.items];
      const newProviderTripsItems = providerTripsItems.filter(
        (item) => item.tripLegReferenceID !== action.payload,
      );
      state.providerTrips = {
        ...state.providerTrips,
        itemCount: state.providerTrips.itemCount - 1,
        items: newProviderTripsItems,
      };
    },
    removeFromTripLegs: (state: TripState, action: { payload: string }) => {
      const tripLegsItems = [...state.tripLegs.items];
      const newTripLegsItems = tripLegsItems.filter(
        (item) => item.tripLegReferenceID !== action.payload,
      );
      state.tripLegs = {
        ...state.tripLegs,
        itemCount: state.tripLegs.itemCount - 1,
        items: newTripLegsItems,
      };
    },
    setTripLegs: (
      state: TripState,
      action: { payload: PagedList<TripLegResponseItem> },
    ) => {
      state.tripLegs = action.payload;
    },
    setLoadingTripLegs: (state: TripState, action: { payload: boolean }) => {
      state.loadingTripLegs = action.payload;
    },
    setLoadedTripLegs: (state: TripState, action: { payload: boolean }) => {
      state.loadedTripLegs = action.payload;
    },
    upsertTripLegsWithPayableAmount: (
      state: TripState,
      action: {
        payload: { tripLegReferenceID: string; payableAmount: string };
      },
    ) => {
      const tripLegs = [...state.tripLegs.items];
      const newTripLegs = tripLegs.map((item) => {
        if (item.tripLegReferenceID === action.payload.tripLegReferenceID) {
          return {
            ...item,
            payableAmount: action.payload.payableAmount,
          };
        }
        return item;
      });
      state.tripLegs = {
        ...state.tripLegs,
        items: newTripLegs,
      };
    },
    setTripLegLogTime: (
      state: TripState,
      action: {
        payload: {
          tripLegReferenceID: string;
          status: TripLegStatusBC;
          time: string;
        };
      },
    ) => {
      const index = state.tripLegs.items.findIndex(
        (item) => item.tripLegReferenceID === action.payload.tripLegReferenceID,
      );
      if (index !== -1 && state.tripLegs.items[index]) {
        const tripLeg = state.tripLegs.items[index];
        const logIndex = tripLeg.tripLegStatusLogs?.findIndex(
          (log) => log.tripLegStatusBC === action.payload.status,
        );

        if (logIndex && logIndex !== -1 && tripLeg.tripLegStatusLogs) {
          tripLeg.tripLegStatusLogs[logIndex] = {
            ...tripLeg.tripLegStatusLogs[logIndex],
            createdOnUTC: action.payload.time,
          };
        } else {
          const newLogEntry: TripLegStatusLogEntry = {
            tripLegStatusLogID: 0,
            tripLegID: tripLeg.tripLegID ?? 0,
            tripLegStatusID: 0,
            createdByUserID: 0,
            createdOnUTC: action.payload.time,
            tripLegStatusBC: action.payload.status,
          };
          state.tripLegs.items[index].tripLegStatusLogs?.push(newLogEntry);
        }
      }
    },
    setTripLegError: (state: TripState, action: { payload: string | null }) => {
      state.tripLegError = action.payload;
    },
    updateTripLegStatus: (
      state: TripState,
      action: {
        payload: {
          tripLegReferenceID: string;
          status: TripLegStatusBC;
        };
      },
    ) => {
      const index = state.tripLegs.items.findIndex(
        (item) => item.tripLegReferenceID === action.payload.tripLegReferenceID,
      );
      if (index !== -1 && state.tripLegs.items[index]) {
        state.tripLegs.items[index].tripLegStatusBC = action.payload.status;
      }
    },
    setTripLegAddOns: (
      state: TripState,
      action: { payload: TripLegAddOnLineItem[] | null },
    ) => {
      state.tripLegAddOns = action.payload;
    },
    setLoadingTripLegAddOns: (
      state: TripState,
      action: { payload: boolean },
    ) => {
      state.loadingTripLegAddOns = action.payload;
    },
    setTripLegAddOnsForLeg: (
      state: TripState,
      action: {
        payload: { tripLegID: number; addOns: TripLegAddOnLineItem[] };
      },
    ) => {
      state.tripLegAddOnsMap[action.payload.tripLegID] = action.payload.addOns;
    },
  },
});

export const {
  setProviderTrips,
  setLoadingProviderTrips,
  setLoadedProviderTrips,
  setTripEligibleDriverList,
  setLoadingTripEligibleDrivers,
  setLoadedTripEligibleDrivers,
  setAssigningDriverToTrip,
  setAssignedDriverToTrip,
  setAssigningVehicleToTrip,
  setAssignedVehicleToTrip,
  setTripLegEvent,
  setTripLegExceptionEvent,
  setTripLegDiscussionEvent,
  attestTripLeg,
  upsertTripLeg,
  upsertTripLegException,
  upsertProviderTrip,
  upsertProviderDriverAssigned,
  removeFromTripLegs,
  removeFromProviderTrips,
  setTripLegs,
  setLoadingTripLegs,
  setLoadedTripLegs,
  upsertTripLegsWithPayableAmount,
  setTripLegLogTime,
  setTripLegError,
  updateTripLegStatus,
  setUpdatingTripLegLogID,
  setUpdatingTripLegLogStatus,
  setTripLegAddOns,
  setLoadingTripLegAddOns,
  setTripLegAddOnsForLeg,
} = slice.actions;

export const fetchTripLegsV2Async =
  (params: TripLegRequestV2) => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripLegs(true));
    dispatch(setLoadedTripLegs(false));
    tripService.getTripLegsV2(params).subscribe({
      next: (response) => {
        dispatch(setTripLegs(response));
        dispatch(setLoadingTripLegs(false));
        dispatch(setLoadedTripLegs(true));
      },
      error: (err) => {
        dispatch(setLoadingTripLegs(false));
        dispatch(setLoadedTripLegs(false));
      },
    });
  };

export const fetchAllocatedTripLegsAsync =
  (
    providerID: number,
    pageSize: number,
    page: number,
    sortModel: TripSortModel | null,
    assigned: boolean,
    unassigned: boolean,
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(setLoadingProviderTrips(true));
    dispatch(setLoadedProviderTrips(false));
    tripService
      .findAllocatedTrips(
        providerID,
        pageSize,
        page,
        sortModel,
        assigned,
        unassigned,
      )
      .subscribe({
        next: (triplegs) => {
          const allocatedTripLegs = triplegs.items.filter(
            (tripleg) => tripleg.tripLegStatusBC !== 'OPEN',
          );
          const allocatedTrips: PagedList<TripLeg> = {
            ...triplegs,
            items: allocatedTripLegs,
          };
          dispatch(setProviderTrips(allocatedTrips));
          dispatch(setLoadingProviderTrips(false));
          dispatch(setLoadedProviderTrips(true));
        },
        error: (err) => {
          dispatch(setLoadingProviderTrips(false));
          dispatch(setLoadedProviderTrips(true));
        },
      });
  };

export const fetchTripEligibleDriverListAsync =
  (
    providerID: number,
    tripLegID: string,
    pageSize: number,
    page: number,
    filter: string,
    search: string,
    sortModel: SortModel | null,
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(setLoadingTripEligibleDrivers(true));
    dispatch(setLoadedTripEligibleDrivers(false));
    tripService
      .findTripEligibleDrivers(
        providerID,
        tripLegID,
        pageSize,
        page,
        filter,
        search,
        sortModel,
      )
      .subscribe({
        next: (driverList) => {
          dispatch(setTripEligibleDriverList(driverList));
          dispatch(setLoadingTripEligibleDrivers(false));
          dispatch(setLoadedTripEligibleDrivers(true));
        },
        error: (err) => {
          dispatch(setLoadingTripEligibleDrivers(false));
          dispatch(setLoadedTripEligibleDrivers(false));
        },
      });
  };

export const assignDriverToTripAsync =
  (driverID: number, tripLegReferenceID: string, updateOnly?: boolean) =>
  (dispatch: AppDispatch) => {
    dispatch(setAssigningDriverToTrip(true));
    dispatch(setAssignedDriverToTrip(false));
    tripService
      .assignDriverToTrip(driverID, tripLegReferenceID, updateOnly)
      .subscribe({
        next: () => {
          dispatch(setAssigningDriverToTrip(false));
          dispatch(setAssignedDriverToTrip(true));
        },
        error: () => {
          dispatch(setAssigningDriverToTrip(false));
          dispatch(setAssignedDriverToTrip(false));
        },
      });
  };

export const assignVehicleToTripAsync =
  (vehicleID: number, tripLegReferenceID: string, updateOnly?: boolean) =>
  (dispatch: AppDispatch) => {
    dispatch(setAssigningVehicleToTrip(true));
    dispatch(setAssignedVehicleToTrip(false));
    tripService
      .assignVehicleToTrip(vehicleID, tripLegReferenceID, updateOnly)
      .subscribe({
        next: () => {
          dispatch(setAssigningVehicleToTrip(false));
          dispatch(setAssignedVehicleToTrip(true));
        },
        error: () => {
          dispatch(setAssigningVehicleToTrip(false));
          dispatch(setAssignedVehicleToTrip(false));
        },
      });
  };

export const fetchGetTripLegsAsync =
  (providerID: number, page: number, pageSize: number) =>
  (dispatch: AppDispatch) => {
    dispatch(setLoadingTripLegs(true));
    dispatch(setLoadedTripLegs(false));
    tripService
      .getTripLegsV2({
        transportationProviderID: [providerID.toString()],
        page,
        pageSize,
      })
      .subscribe({
        next: (response) => {
          dispatch(setTripLegs(response));
          dispatch(setLoadingTripLegs(false));
          dispatch(setLoadedTripLegs(true));
        },
        error: (err) => {
          dispatch(setLoadingTripLegs(false));
          dispatch(setLoadedTripLegs(false));
        },
      });
  };

export const fetchTripLegAddOnsAsync =
  (tripLegID: number) => (dispatch: AppDispatch) => {
    dispatch(setLoadingTripLegAddOns(true));
    tripService.getTripLegByID(tripLegID).subscribe({
      next: (tripLegData) => {
        dispatch(setTripLegAddOns(tripLegData.tripLegAddOnLineItems || null));
        dispatch(setLoadingTripLegAddOns(false));
        if (tripLegData.tripLegAddOnLineItems) {
          dispatch(
            setTripLegAddOnsForLeg({
              tripLegID,
              addOns: tripLegData.tripLegAddOnLineItems,
            }),
          );
        }
      },
      error: (err) => {
        dispatch(setTripLegAddOns(null));
        dispatch(setLoadingTripLegAddOns(false));
      },
    });
  };

export const updateTripLegDriverAsync =
  (
    driverID: number,
    tripLegReferenceID: string,
    providerId: number | null | undefined,
    updateOnly?: boolean,
  ) =>
  (dispatch: AppDispatch) => {
    tripService
      .assignDriverToTrip(driverID, tripLegReferenceID, updateOnly)
      .subscribe({
        next: (driver) => {
          if (providerId) {
            dispatch(
              fetchTripLegsV2Async({
                transportationProviderID: [providerId.toString()],
                attested: false,
                includeTripLegStatusLogs: true,
                includeTripLegAddOnLineItems: true,
                page: 1,
                pageSize: 1000,
                tripLegStatusBCs: [...tripStatusAttestationList],
                pickupStartDate: attestationUtils
                  .getStartDate()
                  .toUTC()
                  .toISO(),
                pickupEndDate: DateTime.now().endOf('day').toUTC().toISO(),
              }),
            );
          }
        },
      });
  };

export const submitDiscussionAsync =
  (tripLegID: number, reason: string, providerId: number | null | undefined) =>
  (dispatch: AppDispatch) => {
    tripService.setTripLegUnderDiscussion(tripLegID, reason).subscribe({
      next: () => {
        if (providerId) {
          dispatch(
            fetchTripLegsV2Async({
              transportationProviderID: [providerId.toString()],
              attested: false,
              includeTripLegStatusLogs: true,
              includeTripLegAddOnLineItems: true,
              page: 1,
              pageSize: 1000,
              tripLegStatusBCs: [...tripStatusAttestationList],
              pickupStartDate: attestationUtils.getStartDate().toUTC().toISO(),
              pickupEndDate: DateTime.now().endOf('day').toUTC().toISO(),
            }),
          );
        }
      },
    });
  };

export const clearIsUnderDiscussionAsync =
  (tripLegID: number, providerId: number | null | undefined) =>
  (dispatch: AppDispatch) => {
    tripService.clearTripLegUnderDiscussion(tripLegID, 'NOCHANGE').subscribe({
      next: () => {
        if (providerId) {
          dispatch(
            fetchTripLegsV2Async({
              transportationProviderID: [providerId.toString()],
              attested: false,
              includeTripLegStatusLogs: true,
              includeTripLegAddOnLineItems: true,
              page: 1,
              pageSize: 1000,
              tripLegStatusBCs: [...tripStatusAttestationList],
              pickupStartDate: attestationUtils.getStartDate().toUTC().toISO(),
              pickupEndDate: DateTime.now().endOf('day').toUTC().toISO(),
            }),
          );
        }
      },
    });
  };

export const updateTripLegDriverAction =
  (
    status: TripLegStatusBC,
    passengerUUID: string,
    tripLegReferenceID: string,
    tripLegStatusDateTimeUTC: string,
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(setUpdatingTripLegLogID(tripLegReferenceID));
    dispatch(setUpdatingTripLegLogStatus(status));
    tripLegReservationService
      .updateTripLegDriverAction(
        status,
        passengerUUID,
        tripLegReferenceID,
        tripLegStatusDateTimeUTC,
      )
      .subscribe({
        next: () => {
          dispatch(
            setTripLegLogTime({
              tripLegReferenceID,
              status,
              time: tripLegStatusDateTimeUTC,
            }),
          );
          dispatch(setUpdatingTripLegLogID(null));
          dispatch(setUpdatingTripLegLogStatus(null));
        },
        error: (err) => {
          dispatch(setTripLegError(err?.message));
          dispatch(setUpdatingTripLegLogID(null));
          dispatch(setUpdatingTripLegLogStatus(null));
        },
      });
  };

export const updateTripLegLogAsync =
  (
    status: TripLegStatusBC,
    passengerUUID: string,
    tripLegReferenceID: string,
    tripLegStatusDateTimeUTC: string,
    providerId: number | null | undefined,
    timestampExists?: boolean,
    currentTripLegStatus?: TripLegStatusBC,
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(setUpdatingTripLegLogID(tripLegReferenceID));
    dispatch(setUpdatingTripLegLogStatus(status));

    const shouldUseUpdateLog =
      timestampExists &&
      currentTripLegStatus &&
      hasReachedStatus(currentTripLegStatus, status);

    if (shouldUseUpdateLog) {
      tripLegReservationService
        .updateTripLegLog(
          status,
          passengerUUID,
          tripLegReferenceID,
          tripLegStatusDateTimeUTC,
        )
        .subscribe({
          next: () => {
            dispatch(
              setTripLegLogTime({
                tripLegReferenceID,
                status,
                time: tripLegStatusDateTimeUTC,
              }),
            );
            dispatch(setUpdatingTripLegLogID(null));
            dispatch(setUpdatingTripLegLogStatus(null));
          },
          error: (err) => {
            if (
              !err?.message ||
              err?.message?.match(
                /has not yet transitioned to the (\w+) status/,
              )
            ) {
              dispatch(
                updateTripLegDriverAction(
                  status,
                  passengerUUID,
                  tripLegReferenceID,
                  tripLegStatusDateTimeUTC,
                ),
              );
            } else {
              dispatch(setTripLegError(err.message));
              dispatch(setUpdatingTripLegLogID(null));
              dispatch(setUpdatingTripLegLogStatus(null));
            }
          },
        });
    } else {
      tripLegReservationService
        .updateTripLegDriverAction(
          status,
          passengerUUID,
          tripLegReferenceID,
          tripLegStatusDateTimeUTC,
        )
        .subscribe({
          next: () => {
            dispatch(
              setTripLegLogTime({
                tripLegReferenceID,
                status,
                time: tripLegStatusDateTimeUTC,
              }),
            );
            dispatch(setUpdatingTripLegLogID(null));
            dispatch(setUpdatingTripLegLogStatus(null));
            dispatch(
              updateTripLegLogAsync(
                status,
                passengerUUID,
                tripLegReferenceID,
                tripLegStatusDateTimeUTC,
                providerId,
                true,
                status,
              ),
            );
          },
          error: (err) => {
            if (
              err?.message?.match(
                /Invalid action .+ on trip leg of status .+, must be one of:/,
              )
            ) {
              dispatch(
                updateTripLegLogAsync(
                  status,
                  passengerUUID,
                  tripLegReferenceID,
                  tripLegStatusDateTimeUTC,
                  providerId,
                  true,
                  status,
                ),
              );
            } else {
              dispatch(setTripLegError(err?.message));
              dispatch(setUpdatingTripLegLogID(null));
              dispatch(setUpdatingTripLegLogStatus(null));
            }
          },
        });
    }
  };

export const overrideTripLegStatus =
  (
    passengerUUID: string,
    tripLegReferenceID: string,
    status: TripLegStatus,
    tripLegStatusDateTimeUTC: string,
    providerId: number | null | undefined,
    tripLegStatusReasonBCs?: string[] | null,
  ) =>
  (dispatch: AppDispatch, getState: () => any) => {
    tripLegReservationService
      .overrideTripLegStatus(
        passengerUUID,
        tripLegReferenceID,
        status,
        tripLegStatusDateTimeUTC,
        tripLegStatusReasonBCs,
      )
      .subscribe({
        next: () => {
          if (status === TripLegStatus.CanceledLate && providerId) {
            const state = getState();
            const tripLegs = state.trip.tripLegs.items;
            const tripLegExceptions = selectTripLegExceptions(state);

            const tripLeg = tripLegs.find(
              (item: any) => item.tripLegReferenceID === tripLegReferenceID,
            );

            const readyInvoiceException = tripLegExceptions.find(
              (exception) =>
                exception.tripLegExceptionBC ===
                TripLegExceptionBC.ReadyInvoice,
            );

            if (
              tripLeg?.tripLegID &&
              readyInvoiceException?.tripLegExceptionID
            ) {
              dispatch(
                setExceptions(
                  [tripLeg.tripLegID],
                  providerId,
                  readyInvoiceException.tripLegExceptionID,
                ),
              );
            }
          }

          // Update local state instead of refetching all data
          const statusBC =
            status === TripLegStatus.CanceledLate
              ? TripLegStatusBC.CanceledLate
              : status === TripLegStatus.NoLoad
              ? TripLegStatusBC.NoLoad
              : TripLegStatusBC.Completed;

          dispatch(
            updateTripLegStatus({
              tripLegReferenceID,
              status: statusBC,
            }),
          );

          dispatch(
            setTripLegLogTime({
              tripLegReferenceID,
              status: statusBC,
              time: tripLegStatusDateTimeUTC,
            }),
          );
        },
        error: (err) => {
          dispatch(setTripLegError(err.message));
        },
      });
  };

export const overrideTripLegStatusCanceledLateToNoLoad =
  (
    passengerUUID: string,
    tripLegReferenceID: string,
    status: TripLegStatus,
    onSiteDateTimeUTC: string,
    noLoadDateTimeUTC: string,
    providerId: number | null | undefined,
  ) =>
  (dispatch: AppDispatch, getState: () => any) => {
    tripLegReservationService
      .overrideTripLegStatus(
        passengerUUID,
        tripLegReferenceID,
        status,
        onSiteDateTimeUTC,
      )
      .subscribe({
        next: () => {
          tripLegReservationService
            .overrideTripLegStatus(
              passengerUUID,
              tripLegReferenceID,
              TripLegStatus.NoLoad,
              noLoadDateTimeUTC,
              ['NOPASSENGERFORDRIVER'],
            )
            .subscribe({
              next: () => {
                // Update local state instead of refetching all data
                dispatch(
                  setTripLegLogTime({
                    tripLegReferenceID,
                    status: TripLegStatusBC.Onsite,
                    time: onSiteDateTimeUTC,
                  }),
                );

                dispatch(
                  updateTripLegStatus({
                    tripLegReferenceID,
                    status: TripLegStatusBC.NoLoad,
                  }),
                );

                dispatch(
                  setTripLegLogTime({
                    tripLegReferenceID,
                    status: TripLegStatusBC.NoLoad,
                    time: noLoadDateTimeUTC,
                  }),
                );
              },
            });
        },
        error: (err) => {
          dispatch(setTripLegError(err.message));
        },
      });
  };

export const overrideTripLegStatusOnsiteToNoLoad =
  (
    passengerUUID: string,
    tripLegReferenceID: string,
    onSiteDateTimeUTC: string,
    noLoadDateTimeUTC: string,
    providerId: number | null | undefined,
  ) =>
  (dispatch: AppDispatch) => {
    if (providerId) {
      dispatch(
        updateTripLegDriverAction(
          TripLegStatusBC.Onsite,
          passengerUUID,
          tripLegReferenceID,
          onSiteDateTimeUTC,
        ),
      );
    }
    tripLegReservationService
      .overrideTripLegStatus(
        passengerUUID,
        tripLegReferenceID,
        TripLegStatus.NoLoad,
        noLoadDateTimeUTC,
      )
      .subscribe({
        next: () => {
          // Update local state instead of refetching all data
          dispatch(
            updateTripLegStatus({
              tripLegReferenceID,
              status: TripLegStatusBC.NoLoad,
            }),
          );

          dispatch(
            setTripLegLogTime({
              tripLegReferenceID,
              status: TripLegStatusBC.NoLoad,
              time: noLoadDateTimeUTC,
            }),
          );
        },
        error: (err) => {
          dispatch(setTripLegError(err.message));
        },
      });
  };

export const overrideTripLegStatusNoLoadToCompleted =
  (
    passengerUUID: string,
    tripLegReferenceID: string,
    onBoardDateTimeUTC: string,
    completedDateTimeUTC: string,
    providerId: number | null | undefined,
  ) =>
  (dispatch: AppDispatch) => {
    tripLegReservationService
      .overrideTripLegStatus(
        passengerUUID,
        tripLegReferenceID,
        TripLegStatus.Onboard,
        onBoardDateTimeUTC,
      )
      .subscribe({
        next: () => {
          if (providerId) {
            dispatch(
              updateTripLegDriverAction(
                TripLegStatusBC.Completed,
                passengerUUID,
                tripLegReferenceID,
                completedDateTimeUTC,
              ),
            );
          }
        },
        error: (err) => {
          dispatch(setTripLegError(err.message));
        },
      });
  };

export const overrideTripLegStatusCanceledLateToCompleted =
  (
    passengerUUID: string,
    tripLegReferenceID: string,
    onSiteDateTimeUTC: string,
    onBoardDateTimeUTC: string,
    completedDateTimeUTC: string,
    providerId: number | null | undefined,
  ) =>
  (dispatch: AppDispatch) => {
    tripLegReservationService
      .overrideTripLegStatus(
        passengerUUID,
        tripLegReferenceID,
        TripLegStatus.Onsite,
        onSiteDateTimeUTC,
      )
      .subscribe({
        next: () => {
          if (providerId) {
            dispatch(
              updateTripLegDriverAction(
                TripLegStatusBC.Onboard,
                passengerUUID,
                tripLegReferenceID,
                onBoardDateTimeUTC,
              ),
            );
            dispatch(
              updateTripLegDriverAction(
                TripLegStatusBC.Completed,
                passengerUUID,
                tripLegReferenceID,
                completedDateTimeUTC,
              ),
            );
          }
        },
        error: (err) => {
          dispatch(setTripLegError(err.message));
        },
      });
  };

export const clearException =
  (
    tripLegIDs: number[] | number,
    providerId: number | null | undefined,
    callback?: () => void,
  ) =>
  (dispatch: AppDispatch) => {
    const tripLegIDArray = Array.isArray(tripLegIDs)
      ? tripLegIDs
      : [tripLegIDs];
    forkJoin(
      tripLegIDArray.map((tripLegID) => tripService.clearException(tripLegID)),
    ).subscribe({
      next: (results) => {
        if (callback) {
          callback();
        }
      },
    });
  };

export const setExceptions =
  (
    tripLegIDs: number[],
    providerId: number | null | undefined,
    tripLegExceptionID: number,
    callback?: () => void,
  ) =>
  (dispatch: AppDispatch) => {
    forkJoin(
      tripLegIDs.map((tripLegID) =>
        tripService.setException(tripLegID, tripLegExceptionID),
      ),
    ).subscribe({
      next: (results) => {
        if (callback) {
          callback();
        }

        dispatch(
          upsertTripLegException({
            tripLegID: results[0].tripLegID!,
            tripLegExceptionID: tripLegExceptionID,
          }),
        );
      },
    });
  };

export const selectProviderTrips = (state: { trip: TripState }) =>
  state.trip.providerTrips;
export const selectLoadingProviderTrips = (state: { trip: TripState }) =>
  state.trip.loadingProviderTrips;
export const selectLoadedProviderTrips = (state: { trip: TripState }) =>
  state.trip.loadedProviderTrips;
export const selectTripEligibleDriverList = (state: { trip: TripState }) =>
  state.trip.tripEligibleDriverList;
export const selectLoadingTripEligibleDrivers = (state: { trip: TripState }) =>
  state.trip.loadingTripEligibleDrivers;
export const selectLoadedTripEligibleDrivers = (state: { trip: TripState }) =>
  state.trip.loadedTripEligibleDrivers;
export const selectAssigningDriverToTrip = (state: { trip: TripState }) =>
  state.trip.assigningDriverToTrip;
export const selectAssignedDriverToTrip = (state: { trip: TripState }) =>
  state.trip.assignedDriverToTrip;
export const selectAssigningVehicleToTrip = (state: { trip: TripState }) =>
  state.trip.assigningVehicleToTrip;
export const selectAssignedVehicleToTrip = (state: { trip: TripState }) =>
  state.trip.assignedVehicleToTrip;
export const selectTripLegs = (state: { trip: TripState }) =>
  state.trip.tripLegs;
export const selectLoadingTripLegs = (state: { trip: TripState }) =>
  state.trip.loadingTripLegs;
export const selectLoadedTripLegs = (state: { trip: TripState }) =>
  state.trip.loadedTripLegs;
export const selectTripLegError = (state: { trip: TripState }) =>
  state.trip.tripLegError;
export const selectUpdatingTripLegLogID = (state: { trip: TripState }) =>
  state.trip.updatingTripLegLogID;
export const selectUpdatingTripLegLogStatus = (state: { trip: TripState }) =>
  state.trip.updatingTripLegLogStatus;
export const selectTripLegEvent = (state: { trip: TripState }) =>
  state.trip.tripLegEvent;
export const selectTripLegExceptionEvent = (state: { trip: TripState }) =>
  state.trip.tripLegExceptionEvent;
export const selectTripLegDiscussionEvent = (state: { trip: TripState }) =>
  state.trip.tripLegDiscussionEvent;
export const selectTripLegAddOns = (state: { trip: TripState }) =>
  state.trip.tripLegAddOns;
export const selectLoadingTripLegAddOns = (state: { trip: TripState }) =>
  state.trip.loadingTripLegAddOns;
export const selectTripLegAddOnsMap = (state: { trip: TripState }) =>
  state.trip.tripLegAddOnsMap;

export default slice.reducer;
